/**
 * @file src/room/sessionOutcome.js
 * @description What became of this room session, watched from the inside and filed once.
 *
 * The signals this feeds are two numbers on the account — momentum (recency-weighted
 * activity, shown to you) and reliability (do you stay, never shown to anyone).
 * What is sent from here is a plain account of a session, and it is deliberately
 * NOT a score: the client states what it saw, the backend decides what that is
 * worth. A client that computed anything would be a client that could never be
 * corrected without every open tab agreeing to be corrected with it.
 *
 * Three rules this file holds:
 *
 * 1. ONE REPORT PER SESSION, EVER. `#sent` latches on the first send and never
 *    unlatches. The unload path, the score reaching its end and an explicit
 *    Leave can all fire in the same second, and the backend takes first-write-
 *    wins anyway — but sending three is three requests from a closing tab, and
 *    the last one is the one most likely to be cut off mid-flight.
 *
 * 2. WE REPORT WHAT WE SAW, NOT WHAT WE CONCLUDE. `unknown` is the answer for a
 *    peer we lost track of, and it is by far the commonest one — a peer who was
 *    gone before the end could have walked out or could have lost their network,
 *    and from in here those are the same event. Guessing between them would be
 *    inventing evidence.
 *
 * 3. PLATFORM TROUBLE IS RECORDED AS TROUBLE — AND NOTHING ELSE IS. A dropped
 *    socket, or a refusal from the short list in `PLATFORM_FAILURE_CODES`, sets
 *    `sawPlatformIssue`, which discards the whole session for reliability on
 *    every side. A session that broke says nothing about whether anybody would
 *    have stayed, and counting a bad night on the relay as a room full of people
 *    who abandon each other would poison the signal exactly when the platform is
 *    already having one. The list is short in the other direction too: the relay
 *    refuses things routinely and correctly, and treating a rate-limited chat
 *    message as a failure would void a whole room's evidence over nothing.
 */
import { sendSessionReport } from '../api/sessionReportApi.js';
import { resolveViewerAuth } from '../auth/viewerAuth.js';
import { getRoomGrantId } from './roomGrant.js';

/**
 * How long a warmed token is trusted before we stop sending it.
 *
 * Firebase ID tokens last an hour. Past this we send none at all rather than a
 * stale one — the request still authenticates by session cookie, which mycelium
 * accepts directly, and an expired bearer would be REFUSED where no bearer
 * succeeds.
 */
const AUTH_WARM_TTL_MS = 50 * 60 * 1000;

/**
 * Below this, nothing is reported at all.
 *
 * Loading a room and bouncing straight back out is not a session — not a short
 * one, not a failed one, not an early exit. Reporting it would file an early exit
 * against everyone whose page took a moment to settle, and the backend would have
 * no way to tell those from real departures.
 */
const MIN_REPORTABLE_MS = 20 * 1000;

/**
 * The relay refusals that mean the PLATFORM failed, as opposed to the relay
 * doing its job.
 *
 * An allow-list, and short on purpose. `sawPlatformIssue` discards the session
 * for reliability on every side, so treating every refusal as trouble would mean
 * one rate-limited chat message in the first minute silently voided a whole
 * room's evidence — and the relay refuses things routinely and correctly:
 * `CHAT_RATE_LIMITED`, `NOT_SEATED`, `NOT_CREATOR`, `READY_GATE`,
 * `ALREADY_STARTED` are all the rules working, not the platform breaking.
 *
 * What is left is the room going out from under us mid-session, and the server
 * being out of capacity. `sync:connection-lost` is the third case and does not
 * come through here — it is not a refusal, it is the socket giving up.
 */
const PLATFORM_FAILURE_CODES = new Set([
  'ROOM_NOT_FOUND',
  'INVALID_ROOM',
  'ROOM_LIMIT_REACHED'
]);

export class SessionOutcome {
  /** Wall-clock ms accumulated while the room was actually playing. */
  #activeMs = 0;

  /** When the current playing stretch began, or null while gathering/paused. */
  #playingSince = null;

  /** Did the score reach its end while we were still here? The definition of completing. */
  #reachedEnd = false;

  /** Has the room ever actually played? Until it has, there is no session to leave. */
  #started = false;

  /**
   * A token fetched WHILE THE ROOM IS PLAYING, so the send never has to await one.
   *
   * `keepalive` only protects a request that has already reached the network
   * stack. Awaiting `resolveViewerAuth()` first put a promise in front of that —
   * and not a cheap one: once the ID token is over an hour old, `getIdToken()`
   * goes to the network to refresh, which is exactly the state a long session
   * ends in. On `pagehide` the document dies while that promise is still pending
   * and nothing is ever sent, so the commonest early exit — closing the tab —
   * reported nothing at all. The whole point of the unload path.
   */
  #authToken = null;
  #authWarmedAt = 0;
  #authWarming = false;

  /** Anything that makes this session's evidence untrustworthy on every side. */
  #sawPlatformIssue = false;

  /**
   * Everyone we ever shared the room with, by account, and how we last saw them.
   *
   * Keyed by userId rather than clientId on purpose: a reconnect within the grace
   * window is the same person coming back, and keying by socket would file them
   * as one person who left and a second who arrived.
   *
   * Guests have no userId and are not tracked. They have no account to carry a
   * signal, so an entry for them would be evidence with nowhere to go.
   */
  #peers = new Map();

  #sent = false;

  /** Fold a room-state change into the account. Safe to call on every store update. */
  observe({ phase, peers }) {
    const playing = phase === 'playing';
    if (playing && this.#playingSince === null) {
      this.#playingSince = Date.now();
      this.#started = true;
      // Fetch the token now, while there is a document to fetch it with.
      this.#warmAuth();
    } else if (!playing && this.#playingSince !== null) {
      this.#activeMs += Date.now() - this.#playingSince;
      this.#playingSince = null;
    }

    // NOTHING THAT HAPPENS IN THE WAITING ROOM IS A DEPARTURE.
    //
    // Before the score starts there is no session to leave. Someone who looks in
    // on the gathering room, sees what is queued up and decides against it has
    // done nothing wrong — but the peer fold ran from the first `gathering`
    // update, so they vanished from `peers` and were filed as `leftEarly`. If the
    // room then played on for a minute the backend saw a witnessed report and
    // handed them adverse weight for a session they never began.
    //
    // Deciding not to start is not abandoning anyone.
    if (!this.#started) return;

    const present = new Set();
    for (const peer of peers || []) {
      const userId = typeof peer?.userId === 'string' && peer.userId ? peer.userId : null;
      if (!userId) continue;
      present.add(userId);
      // Seen now, so whatever we thought about them is superseded. Someone who
      // dropped and came back has not left.
      this.#peers.set(userId, 'present');
    }
    for (const [userId, state] of this.#peers) {
      if (present.has(userId) || state !== 'present') continue;
      // They were here and now they are not, and the room has not finished. From
      // in here that is all we know — `leftEarly` in the report means exactly
      // "gone before the end", and the backend is the one that decides whether a
      // departure nobody else witnessed is worth anything.
      this.#peers.set(userId, this.#reachedEnd ? 'completed' : 'leftEarly');
    }
  }

  /** The score reached its end with us still in the room. */
  noteReachedEnd() {
    if (this.#reachedEnd) return;
    this.#reachedEnd = true;
    // Everyone still here at the end stayed to the end. Recorded now rather than
    // at send time, because the peer list empties as people close their tabs
    // afterwards and a reader at send time would file all of them as departures.
    for (const [userId, state] of this.#peers) {
      if (state === 'present') this.#peers.set(userId, 'completed');
    }
    // …and FILE IT NOW. The end of the score is the moment the session is over,
    // and it is the only moment we can be sure everyone is still accounted for.
    //
    // Waiting for teardown was wrong: a room whose score has finished is a page
    // somebody is still sitting on, and if they wander off inside the app or just
    // leave the tab open, teardown may not come for hours — or at all. The most
    // ordinary way to complete a session was the one way that reported nothing.
    //
    // `#sent` latches, so the later `pagehide` or `dispose` costs nothing.
    this.report();
  }

  /**
   * The platform itself failed — a dropped socket, or a refusal from the short
   * list above. Never an ordinary refusal: see `PLATFORM_FAILURE_CODES`.
   */
  notePlatformIssue() {
    this.#sawPlatformIssue = true;
  }

  /** A relay refusal. Marks trouble only if the code actually means trouble. */
  noteRelayError(code) {
    if (PLATFORM_FAILURE_CODES.has(code)) this.#sawPlatformIssue = true;
  }

  /**
   * Resolve a token in the background so `report()` never has to wait for one.
   *
   * Fire-and-forget and failure-tolerant: no token simply means the request
   * authenticates by session cookie instead, which is what an unauthenticated
   * guest would do anyway.
   */
  #warmAuth() {
    if (this.#authWarming) return;
    this.#authWarming = true;
    void resolveViewerAuth()
      .then(({ authToken }) => {
        this.#authToken = authToken || null;
        this.#authWarmedAt = Date.now();
      })
      .catch(() => { /* cookie auth is the fallback, and needs nothing from us */ })
      .finally(() => { this.#authWarming = false; });
  }

  /**
   * File it. Idempotent, fire-and-forget, and never awaited by anything in the room.
   *
   * No unload/ordinary distinction, because the transport no longer has one: the
   * send is a `keepalive` fetch, which the browser finishes after the document is
   * gone, so the same call is correct from `pagehide` and from an orderly leave.
   */
  report() {
    if (this.#sent) return;
    const grantId = getRoomGrantId();
    if (!grantId) return;

    const activeMs = this.#activeMs + (this.#playingSince === null ? 0 : Date.now() - this.#playingSince);
    if (activeMs < MIN_REPORTABLE_MS && !this.#reachedEnd) return;

    this.#sent = true;
    const peers = [];
    for (const [userId, state] of this.#peers) {
      peers.push({ userId, outcome: state === 'present' ? 'unknown' : state });
    }

    const fresh = this.#authToken && (Date.now() - this.#authWarmedAt) < AUTH_WARM_TTL_MS;
    void sendSessionReport(
      grantId,
      {
        outcome: this.#reachedEnd ? 'completed' : 'leftEarly',
        activeMs,
        sawPlatformIssue: this.#sawPlatformIssue,
        peers
      },
      // Whatever was warmed, or nothing. NEVER a promise — the send has to reach
      // the network synchronously from here or `pagehide` loses it.
      fresh ? this.#authToken : null
    );
  }

  /** Test seam: what would be sent, without sending it. */
  snapshot() {
    return {
      activeMs: this.#activeMs + (this.#playingSince === null ? 0 : Date.now() - this.#playingSince),
      reachedEnd: this.#reachedEnd,
      sawPlatformIssue: this.#sawPlatformIssue,
      peers: Object.fromEntries(this.#peers),
      sent: this.#sent
    };
  }
}

export const MIN_REPORTABLE_SESSION_MS = MIN_REPORTABLE_MS;
